import { contactMethodOptions } from "constants/contactMethod";
import { distributionLogicValueList } from "constants/paymentPlanConstants";
import { mapPayerNameToCodeForDropdown } from "constants/practiceType";
import { useUserWithAccount } from "context/UserAccountProvider";
import useDocumentTitle from "hooks/useDocumentTitle";
import { usePayerInformation } from "hooks/usePayerInformation";
import { useQuery } from "hooks/useQuery";
import { isEmpty } from "lodash";
import { useEffect, useMemo } from "react";
import { useForm, FormProvider } from "react-hook-form";
import { useGetAccountByIdQuery } from "store/queries/account";

import {
  Header,
  VisitForm,
  Submit,
  PatientInfoForm,
  OrderType,
  ServiceDetails,
  Communication,
  PatientBenefitsSection,
} from "./components";
import { TypesCode, orderTypes } from "./components/Header/constants";
import { PaymentPlan } from "./components/PaymentPlan/PaymentPlan";

export function CreateOrder() {
  const query = useQuery();
  useDocumentTitle("Create Order");
  const accountId = query.get("accountId") || "";
  const { accounts, loadingAccounts } = useUserWithAccount();
  const account = useMemo(() => {
    return accounts?.data.find((acc) => acc.id === accountId);
  }, [accounts]);

  const { data: accountData } = useGetAccountByIdQuery(accountId, {
    skip: isEmpty(accountId),
  });

  const orderType = useMemo(() => {
    const isGfeActive = !!account?.orderTypesVisible?.gfe;
    if (isGfeActive) {
      return orderTypes[TypesCode.GFE].value;
    }
    return orderTypes[TypesCode.Bundled].value;
  }, [account]);

  usePayerInformation();

  const methods = useForm({
    defaultValues: {
      email: null,
      amount: null,
      lastName: null,
      firstName: null,
      dateOfBirth: null,
      totalAmount: null,
      phoneNumber: null,
      accountNumber: null,
      dateOfService: null,
      timeOfService: null,
      serviceDetails: null,
      outOfPocketReason: null,
      accountLocationId: null,
      preferredContactMethod: null,
      estimatedVisitCostForPatient: null,
      orderType,
      accountId,
      payerName: null,
      placeOfService: null,
      locality: null,
      deductible: null,
      outOfPocketMax: null,
      flatCopay: null,
      professionalFlatCopay: null,
      showProfessionalCopay: false,
      professionalFlatCopay2: null,
      showProfessionalCopay2: false,
      coinsurancePercent: null,
      gfeQuoteId: null,
      practiceType: mapPayerNameToCodeForDropdown[0]?.value,
      practiceType2: mapPayerNameToCodeForDropdown[0]?.value,
      orderId: null,
      paymentPlanDuration: null,
      paymentPlanDurationFull: null,
      paymentPlanMinDownPaymentPercent: null,
      paymentPlanEnabled: true,
      eligibilityDetails: null,
      paymentPlanMinDownPaymentFlat: null,
      paymentPlanMinDownPaymentType: null,
      outreachEnabled: true,
      providerDistributionLogic:
        account?.providerDistributionLogic ||
        distributionLogicValueList.BY_PRIORITY,
      facilityCopay: false,
      facilityDeductible: false,
      facilityCoinsurance: false,
      professionalCopay: false,
      professionalDeductible: false,
      professionalCoinsurance: false,
      facilityProfessionalCopay: false,
      professionalProfessionalCopay: true,
      facilityProfessionalCopay2: false,
      professionalProfessionalCopay2: true,
      scheduledFirstCommunicationDate: null,
      scheduledFirstCommunicationSent: false,
      scheduledCommunicationCustomText: null,
      sendDefaultText: true,
      isDownPaymentOnly: false,
      downpaymentOnlyType: null,
      downpaymentOnlyPercent: null,
      downpaymentOnlyFlatAmount: null,
    },
  });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <FormProvider {...methods}>
      <Header />
      {account && (
        <OrderType
          orderTypesVisible={account.orderTypesVisible}
          isFetching={loadingAccounts}
        />
      )}
      <PatientInfoForm />
      <VisitForm account={accountData?.data} />
      {!!accountData?.data && (
        <PatientBenefitsSection account={accountData.data} />
      )}
      <ServiceDetails />
      {!!accountData?.data && <PaymentPlan accountData={accountData} />}
      {account && <Communication account={account} />}
      <Submit />
    </FormProvider>
  );
}
